import type {
  ActivityCockpitSnapshot,
  ManagementTimelineQuery,
} from '@internal/spassu-code-contract';
import { useCallback, useReducer, useRef } from 'react';
import type { CockpitViewModel } from '../model/cockpitViewModel';
import type {
  CockpitDataSource,
  CockpitNavigationPort,
} from './CockpitDataSource';
import {
  cockpitControllerReducer,
  createCockpitControllerState,
} from './cockpitControllerReducer';
import {
  useCockpitSelectionActions,
  useCockpitSnapshotActions,
  useCockpitTimelineActions,
} from './useCockpitControllerActions';
import {
  useCockpitControllerLifecycle,
  useCockpitNavigationInput,
  useCockpitNavigationSync,
} from './useCockpitControllerEffects';
import { useCockpitArtifactContent } from './useCockpitArtifactContent';
import { useCockpitPolling } from './useCockpitPolling';
import { useCockpitSnapshotLoader } from './useCockpitSnapshotLoader';
import { useCockpitTimelineLoader } from './useCockpitTimelineLoader';

const POLL_INTERVAL_MS = 30_000;
const STALE_AFTER_MS = 60_000;

export interface UseCockpitControllerOptions {
  activityId: string;
  dataSource: CockpitDataSource;
  adaptSnapshot(snapshot: ActivityCockpitSnapshot): CockpitViewModel;
  navigation?: CockpitNavigationPort;
  pollIntervalMs?: number;
  staleAfterMs?: number;
}

export function useCockpitController(options: UseCockpitControllerOptions) {
  const {
    activityId,
    dataSource,
    adaptSnapshot,
    navigation,
    staleAfterMs = STALE_AFTER_MS,
  } = options;
  const controller = useControllerState(
    activityId,
    navigation?.initialState?.timelineQuery ?? {},
  );
  const snapshot = useCockpitSnapshotLoader({
    activityId,
    dataSource,
    adaptSnapshot,
    staleAfterMs,
    dispatch: controller.dispatch,
  });
  const timeline = useCockpitTimelineLoader({
    activityId,
    dataSource,
    dispatch: controller.dispatch,
  });
  const artifact = useCockpitArtifactContent({
    activityId,
    dataSource,
    artifacts: controller.state.model?.artifacts ?? [],
    stateRef: controller.stateRef,
    dispatch: controller.dispatch,
  });
  useControllerEffects(options, controller, snapshot, timeline);
  const actions = useControllerActions(controller, snapshot, timeline);
  return {
    ...controller.state,
    loading: controller.state.phase === 'loading',
    ...actions,
    ...artifact,
    selection: { ...controller.state.selection, ...actions.selection },
  };
}

function useControllerState(
  activityId: string,
  timelineQuery: ManagementTimelineQuery,
) {
  const [state, dispatch] = useReducer(
    cockpitControllerReducer,
    createCockpitControllerState(activityId, timelineQuery),
  );
  const stateRef = useRef(state);
  stateRef.current = state;
  const navigationRef = useRef<CockpitNavigationPort>();
  const observedNavigationRef = useRef<string | null>(null);
  const pendingNavigationRef = useRef<string | null>(null);
  return {
    state,
    stateRef,
    navigationRef,
    observedNavigationRef,
    pendingNavigationRef,
    dispatch,
  };
}

function useControllerEffects(
  options: UseCockpitControllerOptions,
  controller: ReturnType<typeof useControllerState>,
  snapshot: ReturnType<typeof useCockpitSnapshotLoader>,
  timeline: ReturnType<typeof useCockpitTimelineLoader>,
): void {
  controller.navigationRef.current = options.navigation;
  useCockpitControllerLifecycle({
    activityId: options.activityId,
    navigationRef: controller.navigationRef,
    dispatch: controller.dispatch,
    loadSnapshot: snapshot.loadSnapshot,
    loadTimeline: timeline.loadTimeline,
    snapshotRequests: snapshot.requests,
    timelineRequests: timeline.requests,
  });
  useCockpitNavigationInput({
    activityId: options.activityId,
    state: controller.state,
    navigationRef: controller.navigationRef,
    observedNavigationRef: controller.observedNavigationRef,
    pendingNavigationRef: controller.pendingNavigationRef,
    dispatch: controller.dispatch,
    loadSnapshot: snapshot.loadSnapshot,
    loadTimeline: timeline.loadTimeline,
  });
  useCockpitNavigationSync(
    controller.state,
    controller.navigationRef,
    controller.pendingNavigationRef,
  );
  const { loadSnapshot } = snapshot;
  const poll = useCallback(
    () => void loadSnapshot(undefined, true),
    [loadSnapshot],
  );
  useCockpitPolling({
    enabled: shouldPoll(controller.state.snapshot),
    intervalMs: options.pollIntervalMs ?? POLL_INTERVAL_MS,
    onPoll: poll,
  });
}

function useControllerActions(
  controller: ReturnType<typeof useControllerState>,
  snapshot: ReturnType<typeof useCockpitSnapshotLoader>,
  timeline: ReturnType<typeof useCockpitTimelineLoader>,
) {
  return {
    ...useCockpitSnapshotActions(controller.stateRef, snapshot.loadSnapshot),
    ...useCockpitTimelineActions({
      stateRef: controller.stateRef,
      dispatch: controller.dispatch,
      loadTimeline: timeline.loadTimeline,
      requests: timeline.requests,
    }),
    selection: useCockpitSelectionActions(
      controller.stateRef,
      controller.dispatch,
    ),
  };
}

function shouldPoll(snapshot: ActivityCockpitSnapshot | null): boolean {
  return Boolean(
    snapshot &&
      !snapshot.historical &&
      snapshot.sections.summary.value?.activity.status !== 'completed',
  );
}
